import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState, ErrorState, Spinner } from "@/components/ui";
import { formatChatDay } from "@/lib/format";
import type { Message } from "./queries";
import { messagesInReadingOrder, useThread } from "./queries";
import { MessageBubble } from "./MessageBubble";

/** How close to the top counts as "show me the older ones". */
const LOAD_MORE_PX = 200;
/** How far from the bottom still counts as "following the conversation". */
const AT_BOTTOM_PX = 120;

/** The local calendar day a message belongs to, for the "Hoy" / "Ayer" chips. */
function dayOf(message: Message | undefined): string | null {
  return message ? new Date(message.timestamp).toDateString() : null;
}

export function Thread({
  conversationId,
  onRetry,
}: {
  conversationId: number;
  onRetry?: (message: Message) => void;
}) {
  const { t } = useTranslation();
  const query = useThread(conversationId);
  const messages = messagesInReadingOrder(query.data?.pages);

  const scrollRef = useRef<HTMLDivElement>(null);
  /** Scroll height captured just before an older page is requested. */
  const anchorRef = useRef<number | null>(null);
  const lastIdRef = useRef<number | null>(null);

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    // A rough guess; `measureElement` corrects each row once it is on screen.
    // Bubbles vary from one line to a photo, so a fixed size would make the
    // scrollbar lie.
    estimateSize: () => 72,
    overscan: 8,
    // A viewport to assume before the container has been measured. Without it
    // a zero-height rect yields zero visible rows, so the thread paints empty
    // until layout settles — and stays empty for good anywhere the measurement
    // never arrives.
    initialRect: { width: 0, height: 600 },
    getItemKey: (index) => messages[index]?.id ?? index,
  });

  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, []);

  // Opening a thread puts you at the newest message, the way opening WhatsApp
  // does. Landing at the top of a fifteen-year relationship would be absurd.
  useEffect(() => {
    lastIdRef.current = null;
    requestAnimationFrame(scrollToBottom);
  }, [conversationId, scrollToBottom]);

  useEffect(() => {
    const newest = messages.at(-1);
    if (!newest || newest.id === lastIdRef.current) return;
    const first = lastIdRef.current === null;
    lastIdRef.current = newest.id;

    const element = scrollRef.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    // Only follow if they were already at the bottom. Yanking someone back
    // down while they are reading history is worse than a missed message,
    // because they cannot tell what moved.
    if (first || distance < AT_BOTTOM_PX) requestAnimationFrame(scrollToBottom);
  }, [messages, scrollToBottom]);

  /**
   * Hold the reading position when older messages are prepended.
   *
   * Without this the content above grows and whatever the agent was reading
   * jumps off the screen — the single most common way an infinite-upward list
   * gets shipped broken.
   */
  useLayoutEffect(() => {
    const element = scrollRef.current;
    const anchor = anchorRef.current;
    if (!element || anchor === null || query.isFetchingNextPage) return;
    element.scrollTop += element.scrollHeight - anchor;
    anchorRef.current = null;
  }, [messages.length, query.isFetchingNextPage]);

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element || !query.hasNextPage || query.isFetchingNextPage) return;
    if (element.scrollTop > LOAD_MORE_PX) return;
    anchorRef.current = element.scrollHeight;
    void query.fetchNextPage();
  }, [query]);

  if (query.isPending) return <Spinner label={t("inbox.loadingMessages")} />;
  if (query.isError) {
    return (
      <ErrorState
        message={(query.error as Error).message}
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (messages.length === 0) return <EmptyState>{t("inbox.noMessages")}</EmptyState>;

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto px-4 py-3 sm:px-[6%]"
    >
      {query.isFetchingNextPage ? (
        <p className="pb-2 text-center text-xs text-mute-600">
          {t("inbox.loadingOlder")}
        </p>
      ) : null}

      <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
        {virtualizer.getVirtualItems().map((row) => {
          const message = messages[row.index];
          if (!message) return null;
          const previous = messages[row.index - 1];
          const next = messages[row.index + 1];
          const day = dayOf(message);
          // A run is consecutive messages from one side on one day: WhatsApp
          // packs them tight and tails only the last.
          const endsRun =
            next === undefined ||
            next.direction !== message.direction ||
            dayOf(next) !== day;
          return (
            <div
              key={row.key}
              ref={virtualizer.measureElement}
              data-index={row.index}
              className={`absolute inset-x-0 ${endsRun ? "pb-3" : "pb-0.5"}`}
              style={{ transform: `translateY(${row.start}px)` }}
            >
              {dayOf(previous) !== day ? (
                <div className="flex justify-center pt-1 pb-3">
                  <span className="wa-day">{formatChatDay(message.timestamp)}</span>
                </div>
              ) : null}
              <MessageBubble
                message={message}
                tail={endsRun}
                {...(onRetry ? { onRetry } : {})}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
